import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Package, Search, Truck, CheckCircle2, Clock, XCircle, ChevronRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = [
  { key: 'pending', label: 'Order Placed', icon: Clock, description: 'Your order has been received' },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2, description: 'Order confirmed and payment received' },
  { key: 'processing', label: 'Processing', icon: Package, description: 'Your items are being prepared' },
  { key: 'shipped', label: 'Shipped', icon: Truck, description: 'Your order is on the way' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2, description: 'Order delivered successfully' },
];

const OrderTracking = () => {
  const [searchParams] = useSearchParams();
  const [lookupType, setLookupType] = useState('order_number');
  const [searchValue, setSearchValue] = useState('');
  const [order, setOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async (e, value, type) => {
    if (e) e.preventDefault();
    
    if (!value?.trim()) {
      setError('Please enter a search value');
      return;
    }

    setLoading(true);
    setError('');
    setOrder(null);
    setOrders([]);
    setSearched(true);

    try {
      const response = await axios.get(`${API}/orders/track`, {
        params: { [type]: value.trim() }
      });
      
      if (Array.isArray(response.data)) {
        setOrders(response.data);
        if (response.data.length === 1) {
          setOrder(response.data[0]);
        }
      } else {
        setOrder(response.data);
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      if (err.response?.status === 404) {
        setError('No orders found. Please check your order number or email and try again.');
      } else {
        setError(err.response?.data?.detail || 'Failed to find order');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-search if order number is in URL
  useEffect(() => {
    const orderNumber = searchParams.get('order');
    if (orderNumber) {
      setSearchValue(orderNumber);
      setLookupType('order_number');
      handleSearch(null, orderNumber, 'order_number');
    }
  }, [searchParams, handleSearch]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSearch(null, searchValue, lookupType);
  };

  const getStatusIndex = (status) => {
    if (status === 'cancelled') return -1;
    return ORDER_STATUSES.findIndex(s => s.key === status);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'ZAR': 'R',
      'NGN': '₦', 'KES': 'KSh', 'GHS': '₵', 'BWP': 'P'
    };
    return symbols[currency] || '$';
  };

  const renderOrderDetails = (orderData) => {
    const currentStatusIndex = getStatusIndex(orderData.status);
    const isCancelled = orderData.status === 'cancelled';

    return (
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        {/* Order Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-6 border-b border-[#E5E5E5]">
          <div>
            <p className="text-sm text-[#737373] mb-1">Order Number</p>
            <h2 className="font-serif text-2xl font-semibold text-[#0A0A0A]">
              {orderData.order_number}
            </h2>
            <p className="text-sm text-[#737373] mt-1">
              Placed on {formatDate(orderData.created_at)}
            </p>
          </div>
          <div className={`px-4 py-2 text-sm font-medium ${
            isCancelled 
              ? 'bg-red-100 text-red-700' 
              : orderData.status === 'delivered'
                ? 'bg-green-100 text-green-700'
                : 'bg-[#C9A962]/20 text-[#C9A962]'
          }`}>
            {isCancelled ? 'Cancelled' : orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1)}
          </div>
        </div>

        {/* Status Timeline */}
        {!isCancelled && (
          <div className="mb-8">
            <h3 className="font-semibold text-[#0A0A0A] mb-6">Order Status</h3>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#E5E5E5]" />
              <div 
                className="absolute left-4 top-0 w-0.5 bg-[#C9A962] transition-all duration-500"
                style={{ 
                  height: `${Math.max(0, (currentStatusIndex / (ORDER_STATUSES.length - 1)) * 100)}%` 
                }}
              />

              {/* Status Steps */}
              <div className="space-y-6">
                {ORDER_STATUSES.map((status, index) => {
                  const isCompleted = index <= currentStatusIndex;
                  const isCurrent = index === currentStatusIndex;
                  const StatusIcon = status.icon;

                  return (
                    <div key={status.key} className="relative flex items-start gap-4 pl-10">
                      <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        isCompleted 
                          ? 'bg-[#C9A962] text-white' 
                          : 'bg-[#F5F5F5] text-[#737373]'
                      } ${isCurrent ? 'ring-4 ring-[#C9A962]/20' : ''}`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      <div className="pt-1">
                        <p className={`font-medium ${isCompleted ? 'text-[#0A0A0A]' : 'text-[#737373]'}`}>
                          {status.label}
                        </p>
                        <p className="text-sm text-[#737373]">{status.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {isCancelled && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200">
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-500" />
              <div>
                <p className="font-medium text-red-700">Order Cancelled</p>
                <p className="text-sm text-red-600">This order has been cancelled. Please contact us if you have questions.</p>
              </div>
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="mb-8">
          <h3 className="font-semibold text-[#0A0A0A] mb-4">Order Items</h3>
          <div className="space-y-4">
            {orderData.items.map((item, index) => (
              <div key={index} className="flex gap-4 p-4 bg-[#FAFAFA] border border-[#E5E5E5]">
                <div className="w-16 h-16 bg-[#F5F5F5] flex-shrink-0">
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-[#0A0A0A]">{item.name}</h4>
                  <p className="text-sm text-[#737373]">Qty: {item.quantity}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-[#0A0A0A]">
                    {getCurrencySymbol(item.currency)}{(item.price * item.quantity).toFixed(2)}
                  </p>
                  <p className="text-xs text-[#737373]">
                    {getCurrencySymbol(item.currency)}{item.price.toFixed(2)} each
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary & Delivery */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Delivery Address */}
          <div className="p-4 bg-[#FAFAFA] border border-[#E5E5E5]">
            <h3 className="font-semibold text-[#0A0A0A] mb-3">Delivery Address</h3>
            <div className="text-sm text-[#737373] space-y-1">
              <p className="text-[#0A0A0A] font-medium">{orderData.customer_info.name}</p>
              <p>{orderData.customer_info.address}</p>
              <p>{orderData.customer_info.city}</p>
              <p>{orderData.customer_info.phone}</p>
              <p>{orderData.customer_info.email}</p>
            </div>
          </div>

          {/* Order Total */}
          <div className="p-4 bg-[#FAFAFA] border border-[#E5E5E5]">
            <h3 className="font-semibold text-[#0A0A0A] mb-3">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#737373]">Subtotal</span>
                <span className="text-[#0A0A0A]">${orderData.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#737373]">Shipping</span>
                <span className="text-[#0A0A0A]">Free</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-[#E5E5E5] font-semibold">
                <span className="text-[#0A0A0A]">Total</span>
                <span className="text-[#0A0A0A]">${orderData.total.toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
              <div className="flex justify-between text-sm">
                <span className="text-[#737373]">Payment Status</span>
                <span className={`font-medium ${
                  orderData.payment_status === 'paid' ? 'text-green-600' : 'text-[#C9A962]'
                }`}>
                  {orderData.payment_status.charAt(0).toUpperCase() + orderData.payment_status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {orderData.customer_info.notes && (
          <div className="mt-6 p-4 bg-[#FAFAFA] border border-[#E5E5E5]">
            <h3 className="font-semibold text-[#0A0A0A] mb-2">Order Notes</h3>
            <p className="text-sm text-[#737373]">{orderData.customer_info.notes}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-28 pb-16 bg-[#FAFAFA]" data-testid="order-tracking-page">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#0A0A0A] mb-3">
            Track Your Order
          </h1>
          <p className="text-[#737373]">
            Enter your order number or email to check your order status
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white border border-[#E5E5E5] p-6 mb-8">
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="flex gap-4 mb-4">
              <button
                type="button"
                onClick={() => setLookupType('order_number')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                  lookupType === 'order_number'
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-[#F5F5F5] text-[#737373] hover:bg-[#E5E5E5]'
                }`}
              >
                Order Number
              </button>
              <button
                type="button"
                onClick={() => setLookupType('email')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                  lookupType === 'email'
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-[#F5F5F5] text-[#737373] hover:bg-[#E5E5E5]'
                }`}
              >
                Email Address
              </button>
            </div>

            <div>
              <Label className="sr-only">
                {lookupType === 'order_number' ? 'Order Number' : 'Email Address'}
              </Label>
              <div className="flex gap-3">
                <Input
                  type={lookupType === 'email' ? 'email' : 'text'}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={lookupType === 'order_number' ? 'e.g., BY-20260120-ABC123' : 'your@email.com'}
                  className="rounded-none flex-1"
                />
                <Button 
                  type="submit" 
                  className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] gap-2 px-6"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Track
                    </>
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}
          </form>
        </div>

        {/* Results */}
        {searched && !loading && !error && (
          <>
            {/* Multiple orders found */}
            {orders.length > 1 && !order && (
              <div className="bg-white border border-[#E5E5E5] p-6 mb-8">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">
                  Found {orders.length} orders for this email
                </h3>
                <div className="space-y-3">
                  {orders.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setOrder(o)}
                      className="w-full flex items-center justify-between p-4 bg-[#FAFAFA] border border-[#E5E5E5] hover:border-[#C9A962] transition-colors text-left"
                    >
                      <div>
                        <p className="font-medium text-[#0A0A0A]">{o.order_number}</p>
                        <p className="text-sm text-[#737373]">{formatDate(o.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs ${
                          o.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          o.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-[#C9A962]/20 text-[#C9A962]'
                        }`}>
                          {o.status.charAt(0).toUpperCase() + o.status.slice(1)}
                        </span>
                        <ChevronRight className="w-5 h-5 text-[#737373]" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Single order or selected order */}
            {order && (
              <>
                {orders.length > 1 && (
                  <button
                    onClick={() => setOrder(null)}
                    className="flex items-center gap-2 text-sm text-[#737373] hover:text-[#0A0A0A] mb-4"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to all orders
                  </button>
                )}
                {renderOrderDetails(order)}
              </>
            )}

            {/* No orders found */}
            {orders.length === 0 && !order && (
              <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                <Package className="w-12 h-12 text-[#D4D4D4] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0A0A0A] mb-2">No Orders Found</h3>
                <p className="text-[#737373] mb-6">
                  We couldn't find any orders matching your search. Please check your order number or email and try again.
                </p>
                <Button asChild variant="outline" className="rounded-none">
                  <Link to="/store">Continue Shopping</Link>
                </Button>
              </div>
            )}
          </>
        )}

        {/* Help Section */}
        <div className="mt-8 p-6 bg-white border border-[#E5E5E5]">
          <h3 className="font-semibold text-[#0A0A0A] mb-2">Need Help?</h3>
          <p className="text-sm text-[#737373] mb-4">
            If you have questions about your order or need assistance, please contact us.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button asChild variant="outline" className="rounded-none">
              <Link to="/about">Contact Us</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-none">
              <Link to="/store">Continue Shopping</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
